/**
 * Loads a script.
 * @param {string} src - Url of script to load.
 * @returns {Promise}
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

/**
 * Regios DOPP: re-apply discounted prices on cart line rows after drawer/section HTML updates.
 * @see https://regiostech.crisp.help/en/article/dopp-api-theme-interop-tutorial-4adkwj/
 * @param {ParentNode|null} root - e.g. cart-drawer or cart-items element
 */
function regiosDoppFormatMoneyFromAmount(amountMajor) {
  const noCode = regiosDoppFormatMoneyNoCodeFromAmount(amountMajor);
  const code = window.Shopify?.currency?.active || 'USD';
  return `${noCode} ${code}`;
}

function regiosDoppFormatMoneyNoCodeFromAmount(amountMajor) {
  const amountNum = Number(amountMajor || 0);
  const cents = Math.round(amountNum * 100);
  const currency = window.Shopify?.currency?.active || 'USD';
  const value = cents / 100;

  // Prefer narrow symbols so CAD renders as "$" (not "CA$") before appending ISO code.
  try {
    const locale = currency === 'CAD'
      ? 'en-CA'
      : (document.documentElement?.lang || undefined);
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol'
    }).format(value);
  } catch (e) {
    /* noop */
  }

  if (window.Shopify?.formatMoney) {
    const formatted = window.Shopify.formatMoney(cents);
    const normalized = formatted
      .replace(/\u00a0/g, ' ')
      .replace(new RegExp(`^${currency}\\s*\\$`, 'i'), '$')
      .replace(/^[A-Z]{2}\s*\$/i, '$')
      .replace(/\s+[A-Z]{3}$/i, '')
      .trim();
    // If store money format omits symbols (e.g. "{{amount}}"), force a currency-symbol fallback.
    if (/[^\d\s.,-]/.test(normalized)) return normalized;
  }
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency
  });
}

function regiosDoppWritePriceBlock(root, saleAmountMajor, regularAmountMajor) {
  if (!root) return;
  const currentEl = root.querySelector('.price__current');
  const wasEl = root.querySelector('.price__was');
  if (!currentEl || !wasEl) return;

  const sale = Number(saleAmountMajor || 0);
  const regular = Number(regularAmountMajor || 0);
  const discounted = Number.isFinite(sale) && Number.isFinite(regular) && sale < regular;
  currentEl.innerHTML = regiosDoppFormatMoneyNoCodeFromAmount(sale);
  wasEl.innerHTML = discounted ? regiosDoppFormatMoneyNoCodeFromAmount(regular) : '';
  wasEl.classList.toggle('visually-hidden', !discounted);
  if (discounted) {
    wasEl.removeAttribute('aria-hidden');
  } else {
    wasEl.setAttribute('aria-hidden', 'true');
  }
  root.classList.toggle('price--on-sale', discounted);
}

function regiosDoppRenderFreeShipping(root, totalDiscountedAmountMajor) {
  if (!root || typeof totalDiscountedAmountMajor !== 'number' || !Number.isFinite(totalDiscountedAmountMajor)) return;

  const totalDiscountedCents = Math.max(0, Math.round(totalDiscountedAmountMajor * 100));
  const notices = root.querySelectorAll('.free-shipping-notice [data-minimum]');
  if (!notices.length) return;

  notices.forEach((notice) => {
    const minCents = parseInt(notice.getAttribute('data-minimum') || '', 10);
    if (!Number.isInteger(minCents) || minCents <= 0) return;

    const progress = Math.min(100, (totalDiscountedCents / minCents) * 100);
    notice.querySelectorAll('.free-shipping-notice__bar').forEach((bar) => {
      bar.style.setProperty('--progress', `${progress}%`);
    });

    const heading = notice.querySelector('.free-shipping-notice__heading');
    if (!heading) return;

    if (totalDiscountedCents >= minCents) {
      const freeText = notice.getAttribute('data-regios-free-shipping-text') || '';
      heading.innerHTML = `<strong>${freeText}</strong>`;
      return;
    }

    const deficitTemplate = notice.getAttribute('data-regios-free-shipping-deficit-template') || '';
    const deficitCents = Math.max(0, minCents - totalDiscountedCents);
    const deficitFormatted = regiosDoppFormatMoneyFromAmount(deficitCents / 100);

    if (deficitTemplate.includes('__amount__')) {
      heading.innerHTML = deficitTemplate.replace('__amount__', deficitFormatted);
    } else {
      heading.textContent = deficitFormatted;
    }
  });
}

async function regiosDoppRunCartLineInterop(root) {
  const api = window.RegiosDOPP?.api?.v0;
  const interop = api?.themeInterop;
  if (!api?.calculateDiscountedPrices || !root) return;

  const rows = Array.from(root.querySelectorAll('.cart-item[data-product-id][data-variant-id][data-regular-price-cents]'));
  if (!rows.length) return;

  const calcTasks = [];

  rows.forEach(async (row) => {
    const productId = parseInt(row.getAttribute('data-product-id'), 10);
    const variantId = parseInt(row.getAttribute('data-variant-id'), 10);
    const regularPriceInCents = parseInt(row.getAttribute('data-regular-price-cents'), 10);
    if (
      !Number.isInteger(productId) ||
      productId <= 0 ||
      !Number.isInteger(variantId) ||
      variantId <= 0 ||
      !Number.isInteger(regularPriceInCents) ||
      regularPriceInCents < 0
    ) {
      return;
    }

    let compareAtPriceInCents = null;
    const cc = row.getAttribute('data-variant-compare-at-cents');
    if (cc) {
      const n = parseInt(cc, 10);
      if (Number.isInteger(n) && n > regularPriceInCents) compareAtPriceInCents = n;
    }

    let quantity = 1;
    const q = row.getAttribute('data-item-quantity');
    if (q) {
      const n = parseInt(q, 10);
      if (Number.isInteger(n) && n > 0) quantity = n;
    }

    const args = {
      productId,
      variantId,
      regularPriceInCents,
      compareAtPriceInCents,
      quantity
    };

    const handle = row.getAttribute('data-product-handle');
    const vendor = row.getAttribute('data-product-vendor');
    const url = row.getAttribute('data-product-url');
    if (handle) args.handle = handle;
    if (vendor) args.vendor = vendor;
    if (url) args.url = url;

    const unitArgs = Object.assign({}, args, { quantity: 1 });
    const subtotalRoot = row.querySelector('.cart-item__total');
    const unitRoot = row.querySelector('.cart-item__info .price');

    // Single API call: unit prices, then line + cart totals from rounded cents × qty so
    // 3 × $19.00 never drifts to $56.98 from floating multi-qty API amounts.
    const lineTask = api.calculateDiscountedPrices(unitArgs).then((res) => {
      const uSale = res?.salePrice?.amount;
      const uReg = res?.regularPrice?.amount;
      regiosDoppWritePriceBlock(unitRoot, uSale, uReg);

      const unitSaleNum = Number(uSale);
      const unitRegNum = Number(uReg);
      const unitSaleCents = Number.isFinite(unitSaleNum) ? Math.round(unitSaleNum * 100) : 0;
      const unitRegCents = Number.isFinite(unitRegNum) ? Math.round(unitRegNum * 100) : 0;
      const lineSaleTotal = (unitSaleCents * quantity) / 100;
      const lineRegTotal = (unitRegCents * quantity) / 100;
      regiosDoppWritePriceBlock(subtotalRoot, lineSaleTotal, lineRegTotal);

      return unitSaleCents >= 0 ? { lineSaleCents: unitSaleCents * quantity } : null;
    }).catch(() => null);
    calcTasks.push(lineTask);
  });

  if (!calcTasks.length) return;

  const results = await Promise.all(calcTasks);
  let totalCartCents = 0;
  let hasAny = false;

  results.forEach((res) => {
    const lineSaleCents = res?.lineSaleCents;
    if (typeof lineSaleCents === 'number' && Number.isFinite(lineSaleCents) && lineSaleCents >= 0) {
      totalCartCents += lineSaleCents;
      hasAny = true;
    }
  });

  const totalDiscountedAmountMajor = totalCartCents / 100;

  if (!hasAny) return;

  const scope = root.closest('cart-drawer') || root.ownerDocument || document;
  const currencyCode = window.Shopify?.currency?.active || 'USD';
  scope.querySelectorAll('.js-regios-cart-total').forEach((el) => {
    el.innerHTML = `${regiosDoppFormatMoneyNoCodeFromAmount(totalDiscountedAmountMajor)}<span class="price__currency-code">${currencyCode}</span>`;
  });

  regiosDoppRenderFreeShipping(scope, totalDiscountedAmountMajor);
}

window.regiosDoppRefreshCartLinePrices = function regiosDoppRefreshCartLinePrices(root) {
  if (!root || root.dataset?.regiosDoppCartSync === 'false') return;

  const run = () => {
    try {
      regiosDoppRunCartLineInterop(root);
    } catch (e) {
      /* Regios optional */
    }
  };

  if (window.RegiosDOPP?.api?.v0?.themeInterop?.updateOnPagePrices) {
    queueMicrotask(run);
  } else if (typeof window.RegiosDOPP !== 'undefined') {
    window.addEventListener('regios-dopp:api-initialized', run, { once: true });
    requestAnimationFrame(run);
  }
};

/* global DetailsDisclosure, trapFocus, removeTrapFocus */

if (!customElements.get('cart-drawer-disclosure')) {
  class CartDrawerDisclosure extends DetailsDisclosure {
    constructor() {
      super();
      this.openBtn = this.querySelector('summary');
      this.closeBtn = this.querySelector('.js-close');
      this.overlay = document.getElementById('cart-drawer').querySelector('.overlay');
    }

    /**
     * Handles 'click' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleClick(evt) {

      if (!evt.target.matches('.js-close')) return;
      this.close();
    }

    /**
     * Opens the details element.
     */
    open() {
      this.overlay.classList.add('is-visible');
      super.open();
      trapFocus(this);

      // Create event handler variables (so the bound event listeners can be removed).
      this.clickHandler = this.clickHandler || this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();

      // Add event listeners (for while disclosure is open).
      this.addEventListener('click', this.clickHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.overlay.addEventListener('click', this.clickHandler);
    }

    /**
     * Closes the details element.
     */
    close() {
      this.overlay.classList.remove('is-visible');
      super.close();
      removeTrapFocus(this.openBtn);

      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.overlay.removeEventListener('click', this.clickHandler);

    }


  }

  customElements.define('cart-drawer-disclosure', CartDrawerDisclosure);
}

/* global SideDrawer */

if (!customElements.get('cart-drawer')) {
  class CartDrawer extends SideDrawer {
    constructor() {
      super();

      this.cartSummary = this.querySelector('.cart-drawer__summary');
      this.init();
      this.bindEvents();
    }

    disconnectedCallback() {
      document.removeEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.removeEventListener('on:bfcache:load-restore', this.refreshHandler);
      document.removeEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (Shopify.designMode) {
        document.removeEventListener('shopify:section:select', this.sectionSelectHandler);
        document.removeEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      if (this.dcbLoadedHandler) {
        document.removeEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }
    }

    init() {
      const cartIcon = document.getElementById('cart-icon'); // 获取购物车图标元素
      if (cartIcon) { // 如果找到了购物车图标
        cartIcon.setAttribute('role', 'button'); // 设置元素角色为按钮
        cartIcon.setAttribute('aria-haspopup', 'dialog'); // 设置属性表示此按钮弹出一个对话框

        cartIcon.addEventListener('click', (evt) => { // 为购物车图标添加点击事件监听器
          evt.preventDefault(); // 阻止默认点击行为
          this.open(cartIcon); // 调用 open 方法打开购物车
             let operateSetting = $('.overallContent').attr('data-switch');

          if (operateSetting === 'true') {
             Inventory.shoppingCart();
          }
         



        });

        cartIcon.addEventListener('keydown', (evt) => { // 为购物车图标添加键盘事件监听器
          if (evt.key !== ' ') return; // 如果按下的不是空格键，则返回
          evt.preventDefault(); // 阻止默认键盘行为
          this.open(cartIcon); // 调用 open 方法打开购物车
        });
      }
    }



    bindEvents() {
      if (Shopify.designMode) {
        this.sectionSelectHandler = this.handleSectionSelect.bind(this);
        this.sectionDeselectHandler = this.handleSectionDeselect.bind(this);
        document.addEventListener('shopify:section:select', this.sectionSelectHandler);
        document.addEventListener('shopify:section:deselect', this.sectionDeselectHandler);
      }

      this.openDrawerViaEventHandler = this.handleDrawerOpenViaEvent.bind(this);
      this.closeDrawerViaEventHandler = this.close.bind(this, null);
      document.addEventListener('dispatch:cart-drawer:open', this.openDrawerViaEventHandler);
      document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);

      if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
        this.dcbLoadedHandler = this.dcbLoadedHandler || CartDrawer.recalculateCssVarHeights;
        document.addEventListener('shopify:payment_button:loaded', this.dcbLoadedHandler);
      }

      this.refreshHandler = this.refresh.bind(this);
      document.addEventListener('dispatch:cart-drawer:refresh', this.refreshHandler);
      document.addEventListener('on:bfcache:load-restore', this.refreshHandler);
    }

    /**
     * Handle when the section is selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionSelect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.open();
    }

    /**
     * Handle when the section is de-selected in the Theme Editor
     * @param {object} evt - Event object.
     */
    handleSectionDeselect(evt) {
      if (evt.target === this.closest('.shopify-section')) this.close();


    }

    /**
     * Handle when the drawer is opened via an event
     * @param {object} evt - Event object.
     */
    handleDrawerOpenViaEvent(evt) {
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Opens the drawer.
     * @param {Element} [opener] - Element that triggered opening of the drawer.
     * @param {Element} [elementToFocus] - Element to focus after drawer opened.
     */
    open(opener, elementToFocus) {
      // Get the quick add drawer web component, if it's currently open and close it
      const quickAddDrawer = document.querySelector('quick-add-drawer[aria-hidden="false"]');
      const overlay = document.querySelector('.js-overlay.is-visible');

      if (quickAddDrawer) {
        if (overlay) overlay.style.transitionDelay = '200ms';
        quickAddDrawer.close();
      }

      // If the cart drawer is open, wait a few ms for a more optimal ux/animation
      setTimeout(() => {
        super.open(opener, elementToFocus, () => {
          if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
            CartDrawer.recalculateCssVarHeights();
          }
        });

        if (this.cartSummary.classList.contains('cart-drawer--checkout--sticky-true')) {
          CartDrawer.recalculateCssVarHeights();
        }

        if (overlay) overlay.style.transitionDelay = '';
      }, quickAddDrawer ? 200 : 0);
      window.initLazyImages();
      // If the drawer is opened via "Quick Add -> View cart" (without a full refresh),
      // re-sync Regios discounted prices on the already-rendered cart rows.
      setTimeout(() => {
        window.regiosDoppRefreshCartLinePrices?.(this);
      }, 250);
    }

    /**
     * Attempts to refresh any cart items (if present). Failing that, it refreshes the entire cart
     * drawer
     * @param {boolean} [dontRefreshCartItems=false] - Prevents the refresh of cart items, and does
     * a straight refresh of the whole cart
     */
    async refresh(dontRefreshCartItems) {
      try {
        const cartItems = this.querySelector('cart-items');
        if (cartItems && !dontRefreshCartItems) {
          cartItems.refresh();

        } else {
          const response = this.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.renderContents({ sections }, false);
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));
      }
    }

    /**
     * Renders the contents of the specified sections to update.
     * @param {object} data - Cart data object.
     * @param {boolean} [openDrawer=true] - Open the cart drawer after rendering sections.
     */
    async renderContents(data, openDrawer = true) {
      if (!this.scriptsLoaded) {
        if (!document.querySelector(`script[src="${theme.scripts.cartItems}"]`)) {
          await loadScript(theme.scripts.cartItems);
        }

        if (!document.querySelector(`script[src="${theme.scripts.shippingCalculator}"]`) && this.dataset.shippingCalculator) {
          await loadScript(theme.scripts.countryProvinceSelector);
          await loadScript(theme.scripts.shippingCalculator);
        }

        this.scriptsLoaded = true;
      }

      this.getSectionsToRender().forEach((section) => {
        const el = document.getElementById(section.id);
        if (el) {
          el.innerHTML = CartDrawer.getElementHTML(
            data.sections[section.section],
            section.selector
          );
        }
      });

      if (openDrawer && this.getAttribute('open') === null) {
        setTimeout(() => this.open());
      }

      window.initLazyImages();
      window.regiosDoppRefreshCartLinePrices?.(this);
    }

    /**
     * Returns an array of objects containing required section details.
     * @returns {Array}
     */
    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          section: this.closest('.shopify-section').id.replace('shopify-section-', ''),
          selector: 'cart-drawer'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }

    /**
     * Dispatches an event to recalculate the data-css-var-heights on the page. Useful for
     * updating the height of the sticky cart buttons
     */
    static recalculateCssVarHeights() {
      window.requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('on:css-var-height:update'));
      });
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      return tmpl.content.querySelector(selector).innerHTML;
    }
  }


  customElements.define('cart-drawer', CartDrawer);
}
